import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'a1c', 'fastingGlucose',
    'a1cDisplay', 'categoryContainer', 'categoryDot', 'categoryName',
    'categoryDescription', 'eAgMgdl', 'eAgMmol', 'recommendation'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const a1c = parseFloat(this.a1cTarget.value) || 0

    if (a1c <= 0) {
      return
    }

    this.a1cDisplayTarget.textContent = a1c.toFixed(1)

    // Calculate estimated average glucose (eAG)
    // ADAG formula: eAG (mg/dL) = 28.7 x A1C - 46.7
    const eAgMgdl = Math.round(28.7 * a1c - 46.7)
    const eAgMmol = (eAgMgdl / 18).toFixed(1)

    this.eAgMgdlTarget.textContent = eAgMgdl
    this.eAgMmolTarget.textContent = eAgMmol

    // Categorize and update display
    const category = this.getCategory(a1c)
    this.updateDisplay(category)
  }

  getCategory(a1c) {
    if (a1c < 5.7) {
      return {
        name: 'NORMAL',
        color: 'green-500',
        borderColor: 'border-green-500',
        bgColor: 'bg-green-50',
        description: 'Your HbA1c is in the normal range. This indicates healthy blood sugar regulation.',
        recommendation: 'Continue healthy lifestyle habits. Recheck every 3 years or as your doctor recommends.'
      }
    }
    if (a1c < 6.5) {
      return {
        name: 'PREDIABETES',
        color: 'yellow-500',
        borderColor: 'border-yellow-500',
        bgColor: 'bg-yellow-50',
        description: 'Your HbA1c indicates prediabetes. Blood sugar is higher than normal but not yet at diabetes levels.',
        recommendation: 'Lifestyle intervention is highly effective at this stage. ' +
          'Focus on diet (reduce refined carbs), exercise, and weight loss. Recheck annually.'
      }
    }
    if (a1c < 7.0) {
      return {
        name: 'DIABETES (CONTROLLED)',
        color: 'orange-500',
        borderColor: 'border-orange-500',
        bgColor: 'bg-orange-50',
        description: 'Your HbA1c indicates diabetes but is within the general target range for diabetics (<7%).',
        recommendation: 'Continue current management. Work with your healthcare team to maintain or improve control. ' +
          'Some may benefit from tighter control (<6.5%).'
      }
    }
    if (a1c < 8.0) {
      return {
        name: 'DIABETES (ABOVE TARGET)',
        color: 'red-400',
        borderColor: 'border-red-400',
        bgColor: 'bg-red-50',
        description: 'Your HbA1c is above the general target of <7%. There is room for improved blood sugar control.',
        recommendation: 'Discuss with your healthcare provider about optimizing treatment. Review medication adherence, diet, and activity levels.'
      }
    }
    return {
      name: 'DIABETES (UNCONTROLLED)',
      color: 'red-600',
      borderColor: 'border-red-600',
      bgColor: 'bg-red-50',
      description: 'Your HbA1c indicates poorly controlled blood sugar. This increases risk of diabetes complications.',
      recommendation: 'Urgent attention needed. Work closely with your healthcare team to improve control. Medication adjustment is often necessary.'
    }
  }

  updateDisplay(category) {
    this.categoryDotTarget.className = `w-4 h-4 bg-${category.color}`
    this.categoryNameTarget.textContent = category.name
    this.categoryDescriptionTarget.textContent = category.description
    this.recommendationTarget.textContent = category.recommendation
    this.categoryContainerTarget.className = `p-6 ${category.borderColor} border-2 ${category.bgColor}`
  }
}
